/**
 * Connect a catalog row: persist pref, then notify the host.
 */

import type { GpuPluginHost } from "./host.js";
import { toast } from "./host.js";
import type { InferenceModel } from "./types.js";
import {
  catalogConnectionId,
  engineName,
  type Connection,
  type ConnectionKind,
} from "./connections.js";
import type { ForgeClientOptions } from "./client.js";
import { selectSuiteConnection } from "./suite-client.js";
import { PRIMARY_TOOL, saveToolBackend } from "./catalog.js";

export function connectTabForSource(
  sourceTab: string | null | undefined,
): ConnectionKind {
  const t = String(sourceTab || "").trim().toLowerCase();
  if (t === "cloudflare" || t === "cf") return "cloudflare";
  if (t === "civitai" || t === "civitai_red") return "civitai";
  if (t === "forge_endpoint") return "forge_endpoint";
  return "hf";
}

export function catalogRowToConnection(
  tab: "hf" | "cloudflare" | "civitai",
  m: InferenceModel,
): Connection {
  const provider = m.provider || tab;
  const now = new Date().toISOString();
  const engine = engineName(provider, tab);
  const baseName = m.name || m.modelId;
  return {
    id: catalogConnectionId(tab, provider, m.modelId),
    kind: tab,
    sourceTable: "catalog",
    sourceId: m.skuId || m.modelId,
    appOrigin: null,
    label: engine ? `${baseName} · ${engine}` : baseName,
    modelId: m.chatModelId || m.modelId,
    engine: provider,
    modality: m.modality || "other",
    endpointUrl: null,
    status: "catalog",
    ready: true,
    visibility: "private",
    lastHealthAt: null,
    lastUsedAt: now,
    createdAt: now,
    updatedAt: now,
    skuId: m.skuId,
    detail: m.detail || null,
    baseModel: m.baseModel || null,
    civitaiType: m.civitaiType || null,
  };
}

function activeToolId(host: GpuPluginHost): string {
  const app = (host.appId || "").trim();
  return (host.accessHubTool || (app ? PRIMARY_TOOL[app] : "") || "").trim();
}

function rememberToolBackend(host: GpuPluginHost, conn: Connection): Connection {
  const toolId = conn.toolId || activeToolId(host);
  const dumpId = conn.dumpId || conn.skuId || conn.id;
  const stamped: Connection = { ...conn, toolId: toolId || conn.toolId, dumpId };
  const app = (host.appId || "").trim();
  if (app && toolId && dumpId) {
    saveToolBackend(app, {
      toolId,
      dumpId,
      modelId: String(conn.modelId || conn.sourceId || ""),
      name: conn.label,
    });
  }
  return stamped;
}

async function persistPref(host: GpuPluginHost, connectionId: string): Promise<void> {
  if (!host.appId || !host.purpose) return;
  const opts: ForgeClientOptions = {
    forgeApiUrl: host.forgeApiUrl,
    getAccessToken: host.getAccessToken,
  };
  await selectSuiteConnection(opts, {
    appId: host.appId,
    purpose: host.purpose,
    connectionId,
  });
}

export async function connectCatalogModel(
  host: GpuPluginHost,
  tab: "hf" | "cloudflare" | "civitai",
  m: InferenceModel,
): Promise<Connection> {
  const conn = rememberToolBackend(host, {
    ...catalogRowToConnection(tab, m),
    dumpId: m.skuId || m.modelId,
  });
  await host.onConnect?.(conn);
  const tool = conn.toolId ? ` · ${conn.toolId}` : "";
  toast(host, "success", `Selected${tool} · ${conn.label}`);
  host.onReady?.();
  try {
    await persistPref(host, conn.id);
  } catch (e) {
    toast(
      host,
      "info",
      `Using ${conn.label} here · suite pref not saved (${e instanceof Error ? e.message : String(e)})`,
    );
  }
  return conn;
}

export function isCatalogKind(k: ConnectionKind): k is "hf" | "cloudflare" | "civitai" {
  return k === "hf" || k === "cloudflare" || k === "civitai";
}

export async function connectProductionModel(
  host: GpuPluginHost,
  m: InferenceModel,
): Promise<Connection> {
  const source = connectTabForSource(m.source_tab);
  if (isCatalogKind(source)) {
    return connectCatalogModel(host, source, m);
  }
  const now = new Date().toISOString();
  const conn: Connection = {
    id: m.skuId || m.modelId,
    kind: "forge_endpoint",
    sourceTable: "catalog",
    sourceId: m.skuId || m.modelId,
    appOrigin: null,
    label: m.name || m.modelId,
    modelId: m.chatModelId || m.modelId,
    engine: m.provider || null,
    modality: m.modality || "other",
    endpointUrl: null,
    status: "catalog",
    ready: true,
    visibility: "private",
    lastHealthAt: null,
    lastUsedAt: now,
    createdAt: now,
    updatedAt: now,
    skuId: m.skuId,
    detail: m.detail || null,
    baseModel: m.baseModel || null,
    civitaiType: m.civitaiType || null,
  };
  const stamped = rememberToolBackend(host, {
    ...conn,
    dumpId: m.skuId || m.modelId || conn.id,
  });
  await host.onConnect?.(stamped);
  const tool = stamped.toolId ? ` · ${stamped.toolId}` : "";
  toast(host, "success", `Selected${tool} · ${stamped.label}`);
  host.onReady?.();
  try {
    await persistPref(host, stamped.id);
  } catch (e) {
    toast(
      host,
      "info",
      `Using ${stamped.label} here · suite pref not saved (${e instanceof Error ? e.message : String(e)})`,
    );
  }
  return stamped;
}
